import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import toast from 'react-hot-toast';
import BackButtonBottom from '../../components/common/BackButtonBottom';

export default function RiderSettings() {
  const { userProfile, refreshProfile, logout } = useAuth();
  const navigate = useNavigate();

  const [phone, setPhone] = useState(userProfile?.phone || '');
  const [savingPhone, setSavingPhone] = useState(false);

  const [emailNotifs, setEmailNotifs] = useState(userProfile?.notificationPrefs?.email !== false);
  const [smsNotifs, setSmsNotifs] = useState(userProfile?.notificationPrefs?.sms !== false);
  const [pushNotifs, setPushNotifs] = useState(userProfile?.notificationPrefs?.push !== false);
  const [savingPrefs, setSavingPrefs] = useState(false);

  const handleSavePhone = async () => {
    if (!phone || phone.trim().length < 10) {
      toast.error('Enter a valid phone number');
      return;
    }
    if (phone === userProfile?.phone) {
      toast('No change to save', { icon: 'ℹ️' });
      return;
    }
    setSavingPhone(true);
    try {
      await updateDoc(doc(db, 'users', userProfile.id), {
        phone: phone.trim(),
        phoneVerified: false, // Will need to re-verify
        updatedAt: serverTimestamp(),
      });
      await refreshProfile();
      toast.success('Phone number updated. You may be asked to re-verify.');
    } catch (err) {
      toast.error(err.message || 'Failed to update phone');
    }
    setSavingPhone(false);
  };

  const handleSavePrefs = async () => {
    setSavingPrefs(true);
    try {
      await updateDoc(doc(db, 'users', userProfile.id), {
        notificationPrefs: {
          email: emailNotifs,
          sms: smsNotifs,
          push: pushNotifs,
        },
        updatedAt: serverTimestamp(),
      });
      await refreshProfile();
      toast.success('Preferences saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    }
    setSavingPrefs(false);
  };

  const handleSignOut = async () => {
    try { await logout(); navigate('/login'); } catch (err) { toast.error('Logout failed'); }
  };

  return (
    <div className="max-w-[600px] mx-auto px-6 py-8">
      <Link to="/rider" className="text-brand-primary font-semibold text-[0.9rem] mb-6 block">← Back</Link>
      <h1 className="font-serif text-[1.8rem] mb-6">Settings</h1>

      {/* Profile (read-only) */}
      <Section title="Profile">
        <ReadOnlyField label="Name" value={`${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`} />
        <ReadOnlyField label="Email" value={userProfile?.email} />
        <ReadOnlyField label="Rider ID" value={userProfile?.referenceNumber} />
        <p className="text-[0.78rem] text-muted mt-2">
          To change your name or email, please contact support.
        </p>
      </Section>

      {/* Phone */}
      <Section title="Phone Number">
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="text-[0.85rem] font-semibold text-body mb-1 block">Phone</label>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="04xx xxx xxx"
              className="w-full px-3 py-2.5 rounded-[10px] border-[1.5px] border-slate-200 text-[0.95rem]" />
          </div>
          <button onClick={handleSavePhone} disabled={savingPhone || phone === userProfile?.phone} className="px-[18px] py-2.5 rounded-[10px] border-0 text-white font-semibold whitespace-nowrap"
            style={{
              background: phone !== userProfile?.phone ? '#2dd4a8' : '#e2e8f0',
              cursor: phone !== userProfile?.phone ? 'pointer' : 'not-allowed',
            }}>{savingPhone ? 'Saving...' : 'Save'}</button>
        </div>
        {!userProfile?.phoneVerified && userProfile?.phone && (
          <div className="mt-2 text-[0.78rem]" style={{ color: '#92400e' }}>
            ⚠ Your phone number is not verified.
          </div>
        )}
      </Section>

      {/* Notification preferences */}
      <Section title="Notifications">
        <Toggle label="Email notifications" value={emailNotifs} onChange={setEmailNotifs}
          desc="Booking confirmations, receipts, and important updates" />
        <Toggle label="SMS notifications" value={smsNotifs} onChange={setSmsNotifs}
          desc="Trip updates: driver assigned, on the way, arrived" />
        <Toggle label="Push notifications" value={pushNotifs} onChange={setPushNotifs}
          desc="Real-time alerts in your browser/app" />
        <button onClick={handleSavePrefs} disabled={savingPrefs} className="mt-3 px-[18px] py-2.5 rounded-[10px] border-0 bg-brand-primary text-white cursor-pointer font-semibold">{savingPrefs ? 'Saving...' : 'Save Preferences'}</button>
      </Section>

      {/* Security */}
      <Section title="Security">
        <p className="text-[0.85rem] text-body mb-3">
          Manage your security PIN.
        </p>
        <Link to="/set-pin" className="inline-block px-[18px] py-2.5 rounded-[10px] border-[1.5px] border-slate-200 bg-white text-brand-dark no-underline font-semibold text-[0.9rem]">Change PIN</Link>
      </Section>

      {/* Wallet */}
      <Section title="Wallet & Payments">
        <Link to="/rider/wallet" className="inline-block px-[18px] py-2.5 rounded-[10px] border-[1.5px] border-slate-200 bg-white text-brand-dark no-underline font-semibold text-[0.9rem]">Manage Wallet</Link>
      </Section>

      {/* Legal */}
      <Section title="Legal">
        <Link to="/legal" className="text-brand-primary font-semibold text-[0.9rem]">
          Terms of Service & Privacy Policy →
        </Link>
      </Section>

      {/* Sign out */}
      <button onClick={handleSignOut} className="mt-5 w-full px-[18px] py-3 rounded-[10px] border-[1.5px] bg-white cursor-pointer font-bold"
        style={{ borderColor: '#dc2626', color: '#dc2626' }}>Sign Out</button>
      <BackButtonBottom to="/rider" label="Dashboard" />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-5 p-5 bg-white rounded-2xl border border-slate-200">
      <h3 className="mt-0 mb-3 text-[1rem] text-brand-dark">{title}</h3>
      {children}
    </div>
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <div className="flex justify-between py-1.5 text-[0.9rem]">
      <span className="text-body">{label}</span>
      <span className="font-medium">{value || '—'}</span>
    </div>
  );
}

function Toggle({ label, value, onChange, desc }) {
  return (
    <div className="flex justify-between items-start py-2.5 border-b border-slate-100">
      <div className="flex-1">
        <div className="font-semibold text-[0.9rem]">{label}</div>
        {desc && <div className="text-[0.78rem] text-muted mt-0.5">{desc}</div>}
      </div>
      <button onClick={() => onChange(!value)} className="w-11 h-6 rounded-xl border-0 cursor-pointer relative transition-all"
        style={{ background: value ? '#2dd4a8' : '#e2e8f0' }}>
        <span className="absolute w-5 h-5 rounded-full transition-all"
          style={{ top: 2, left: value ? 22 : 2, background: '#fff' }} />
      </button>
    </div>
  );
}